import { FormControl, Input } from '@deckee/deck-hand';
import classNames from 'classnames/bind';
import React, { useCallback, useState } from 'react';
import { debounce } from 'throttle-debounce';
import {
  SHARING_MESSAGE__LOOKING_FOR_SUGGESTIONS,
  SHARING_MESSAGE__NO_SUGGESTIONS_FOUND,
} from '../../../configurations/constants';
import { callEndpoint } from '../../../services/backend';
import useFocus from '../../helpers/focus-hook';
import styles from './AutoComplete.scss';

const cx = classNames.bind(styles);

const getMatchingUsers = async (username) => {
  // need to add some debounce and throttle here.
  if (!username) return [];

  const trimmedUsername =
    username.charAt(0) === '@' ? username.substring(1) : username;

  const matches = await callEndpoint(
    callEndpoint.endpoints.searchForUsers,
    {},
    {
      search: trimmedUsername,
    },
  );

  if (matches) {
    return matches;
  }

  return [];
};

const formatUserSuggestion = (suggestion) =>
  `${suggestion.username ? `@${suggestion.username} - ` : ''}${
    suggestion.title
  }`;

const AutoComplete = ({ onValueChange }) => {
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [, setInputRefFocus] = useFocus();

  const getResults = useCallback(
    async (value) => {
      // load the things we want to show.
      const matches = await getMatchingUsers(value);

      if (matches) {
        setFilteredSuggestions(matches);
        setActiveSuggestionIndex(0);
        setShowSuggestions(true);
        setIsLoading(false);
        onValueChange('');
      }
    },
    [onValueChange],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedGetResults = useCallback(debounce(400, getResults), [
    getResults,
  ]);

  const onChange = async (e) => {
    // this has been split up this way to ensure only the fetch and response are debounced.
    const userInput = e.target.value;
    setIsLoading(true);
    setInput(userInput);

    // load the things we want to show.
    await debouncedGetResults(userInput);
  };

  const onClick = (text, userId) => {
    setFilteredSuggestions([]);
    setInput(text);
    setActiveSuggestionIndex(0);
    setShowSuggestions(false);
    onValueChange(userId);
    setInputRefFocus();
  };

  const SuggestionsListComponent = () =>
    filteredSuggestions.length ? (
      <ul className={cx('auto-complete__suggestions')}>
        {filteredSuggestions.map((suggestion, index) => {
          let className;

          // Flag the active suggestion with a class
          if (index === activeSuggestionIndex) {
            className = cx('auto-complete__suggestion-active');
          }

          return (
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions,jsx-a11y/click-events-have-key-events
            <li
              tabIndex={index}
              className={className}
              key={suggestion.id}
              onClick={() =>
                onClick(formatUserSuggestion(suggestion), suggestion.id)
              }
            >
              {formatUserSuggestion(suggestion)}
            </li>
          );
        })}
      </ul>
    ) : (
      <div className={cx('auto-complete__message')}>
        {SHARING_MESSAGE__NO_SUGGESTIONS_FOUND}
      </div>
    );

  return (
    <div
      className={cx('auto-complete')}
      onBlur={(event) => {
        const { currentTarget } = event;

        setTimeout(() => {
          // Check if the new activeElement is a child of the original container
          if (!currentTarget.contains(document.activeElement)) {
            // You can invoke a callback or add custom logic here
            setShowSuggestions(false);
          }
        }, 0);
      }}
    >
      <FormControl label="Deckee username">
        <Input value={input} onChange={onChange} />
      </FormControl>

      {isLoading || showSuggestions ? (
        <div className={cx('auto-complete__message_and_suggestions')}>
          {isLoading && (
            <div className={cx('auto-complete__message')}>
              {SHARING_MESSAGE__LOOKING_FOR_SUGGESTIONS}
            </div>
          )}
          {!isLoading && showSuggestions && input && (
            <SuggestionsListComponent />
          )}
        </div>
      ) : null}
    </div>
  );
};

export default AutoComplete;
